import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Search, ChevronDown, ChevronRight, Package, Boxes, Layers, AlertTriangle } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n, sym) => (n == null ? '—' : `${sym}${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`);
const fmtPct = (n) => `${Math.round(Number(n || 0) * 100)}%`;
const currencySymbol = (cur) => (cur === 'TRY' ? '₺' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : `${cur || ''} `);

const SORTS = [
  { key: 'breakage_desc', label: 'En çok beden kırığı' },
  { key: 'sizes_desc', label: 'En çok beden' },
  { key: 'sizes_asc', label: 'En az beden' },
  { key: 'instock_desc', label: 'Stokta beden (çok→az)' },
  { key: 'instock_asc', label: 'Stokta beden (az→çok)' },
  { key: 'title_asc', label: 'İsim (A→Z)' },
];

// Beden kırığı bandları (kırık = stok dışı beden oranı). Eşikler backend ile aynı.
const bandOf = (breakagePct) => {
  const b = Number(breakagePct || 0);
  if (b > 0.75) return 'critical';
  if (b > 0.50) return 'high';
  if (b > 0.25) return 'mid';
  return 'healthy';
};
const BANDS = [
  { key: 'healthy', label: 'Sağlıklı', sub: 'kırık ≤ %25', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  { key: 'mid', label: 'Orta', sub: 'kırık %25–50', cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  { key: 'high', label: 'Yüksek', sub: 'kırık %50–75', cls: 'text-orange-700 bg-orange-50 border-orange-200', dot: 'bg-orange-500' },
  { key: 'critical', label: 'Kritik', sub: 'kırık > %75', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
];

export default function High5SkuSizeCoverage() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [stockOnly, setStockOnly] = useState(false);
  const [zeroStockLast, setZeroStockLast] = useState(true);
  const [bandFilter, setBandFilter] = useState('ALL');
  const [sort, setSort] = useState('breakage_desc');
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/sku-feed/size-coverage`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const sym = currencySymbol(payload?.currency);
  const allProducts = payload?.products || [];

  const brands = useMemo(() => {
    const s = new Set();
    allProducts.forEach((p) => p.brand && s.add(p.brand));
    return [...s].sort();
  }, [allProducts]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = allProducts.filter((p) => {
      if (brandFilter !== 'ALL' && p.brand !== brandFilter) return false;
      if (bandFilter !== 'ALL' && bandOf(p.breakagePct) !== bandFilter) return false;
      if (stockOnly && (p.inStockSizes || 0) === 0) return false;
      if (q && !(`${p.title || ''} ${p.brand || ''} ${p.color || ''} ${p.feedId || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
    const base = {
      breakage_desc: (a, b) => (b.breakagePct - a.breakagePct) || (b.totalSizes - a.totalSizes),
      sizes_desc: (a, b) => b.totalSizes - a.totalSizes,
      sizes_asc: (a, b) => a.totalSizes - b.totalSizes,
      instock_desc: (a, b) => b.inStockSizes - a.inStockSizes,
      instock_asc: (a, b) => a.inStockSizes - b.inStockSizes,
      title_asc: (a, b) => String(a.title || '').localeCompare(String(b.title || '')),
    }[sort];
    // İstenirse stoku tamamen sıfır olanları, seçili sıralama ne olursa olsun en alta it.
    const cmp = zeroStockLast
      ? (a, b) => {
          const az = (a.inStockSizes || 0) === 0 ? 1 : 0;
          const bz = (b.inStockSizes || 0) === 0 ? 1 : 0;
          return (az - bz) || base(a, b);
        }
      : base;
    return [...r].sort(cmp);
  }, [allProducts, search, brandFilter, bandFilter, stockOnly, zeroStockLast, sort]);

  const filteredSkus = useMemo(() => rows.reduce((s, p) => s + p.totalSizes, 0), [rows]);

  const toggle = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Boxes className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ürün Beden Dağılımı</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Google SKU feed — her üründe kaç beden var ve kaçı stokta.
                  {payload?.fetchDate ? ` · Feed: ${payload.fetchDate}` : ''}
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Yenile"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Summary stats */}
          {payload?.summary && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Package} color="text-blue-600 bg-blue-50" label="Ürün" value={fmtNumber(payload.summary.products)} />
              <StatCard icon={Layers} color="text-violet-600 bg-violet-50" label="Toplam beden (SKU)" value={fmtNumber(payload.summary.skus)} />
              <StatCard icon={Boxes} color="text-emerald-600 bg-emerald-50" label="Ürün başına ort. beden" value={Number(payload.summary.avgSizes || 0).toFixed(1)} />
            </div>
          )}

          {/* Beden kırığı özet tablosu — tıklanınca filtreler */}
          {payload?.summary?.bands && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-gray-700">Beden Kırığı Dağılımı</h3>
                <span className="text-xs text-gray-400">(kırık = stok dışı beden oranı · banda tıkla → filtrele)</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {BANDS.map((b) => {
                  const cnt = payload.summary.bands[b.key] || 0;
                  const active = bandFilter === b.key;
                  const pct = payload.summary.products ? (cnt / payload.summary.products * 100) : 0;
                  return (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => setBandFilter(active ? 'ALL' : b.key)}
                      className={`text-left rounded-xl border p-3 transition ${b.cls} ${active ? 'ring-2 ring-offset-1 ring-gray-800/40' : 'hover:shadow-sm'}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                        <span className="text-xs font-semibold uppercase tracking-wide">{b.label}</span>
                      </div>
                      <div className="mt-1 text-2xl font-bold tabular-nums">{fmtNumber(cnt)}</div>
                      <div className="text-[11px] opacity-70">{b.sub} · %{pct.toFixed(0)} ürün</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Beden kırığı <strong className="text-red-600">&gt; %75</strong> olan: <strong>{fmtNumber(payload.summary.criticalOver75)}</strong> ürün ·
                kırık ≤ %75 kalan (availability ≥ 0.25): <strong>{fmtNumber(payload.summary.keptProducts)}</strong> ürün / {fmtNumber(payload.summary.keptSkus)} beden
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ürün, marka, renk, kod ara…"
              className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
            <option value="ALL">Tüm markalar</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={bandFilter} onChange={(e) => setBandFilter(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
            <option value="ALL">Tüm beden kırığı</option>
            <option value="critical">Kritik · kırık &gt; %75</option>
            <option value="high">Yüksek · %50–75</option>
            <option value="mid">Orta · %25–50</option>
            <option value="healthy">Sağlıklı · ≤ %25</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={zeroStockLast} onChange={(e) => setZeroStockLast(e.target.checked)} className="rounded" />
            Stok 0 olanları en alta
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={stockOnly} onChange={(e) => setStockOnly(e.target.checked)} className="rounded" />
            Sadece stokta bedeni olanlar
          </label>
          <span className="text-xs text-gray-400 ml-auto">{fmtNumber(rows.length)} ürün · {fmtNumber(filteredSkus)} beden</span>
        </div>

        {/* Body */}
        <div className="p-0">
          {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
          {!selectedBrand?.id && <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>}
          {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}

          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left w-8" />
                    <th className="px-3 py-3 text-left">Ürün</th>
                    <th className="px-3 py-3 text-left">Marka</th>
                    <th className="px-3 py-3 text-center">Beden</th>
                    <th className="px-3 py-3 text-center">Stokta</th>
                    <th className="px-3 py-3 text-center">Stok dışı</th>
                    <th className="px-3 py-3 text-center">Beden kırığı</th>
                    <th className="px-3 py-3 text-right">Fiyat</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const open = expanded.has(p.feedId);
                    const stockRatio = p.totalSizes > 0 ? p.inStockSizes / p.totalSizes : 0;
                    return (
                      <React.Fragment key={p.feedId}>
                        <tr className="border-t border-gray-100 hover:bg-blue-50/30 cursor-pointer" onClick={() => toggle(p.feedId)}>
                          <td className="px-3 py-2 text-gray-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-3">
                              {p.image ? (
                                <img src={p.image} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover border border-gray-100 bg-white" />
                              ) : <div className="w-10 h-10 rounded-lg bg-gray-100" />}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-800 truncate max-w-[360px]">{p.title || p.feedId}</div>
                                <div className="text-xs text-gray-400">{p.color || ''}{p.color && p.feedId ? ' · ' : ''}{p.feedId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{p.brand || '—'}</td>
                          <td className="px-3 py-2 text-center"><span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold tabular-nums">{p.totalSizes}</span></td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className="tabular-nums font-semibold text-emerald-700">{p.inStockSizes}</span>
                              <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                <div className="h-full bg-emerald-500" style={{ width: `${Math.round(stockRatio * 100)}%` }} />
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums text-gray-400">{p.outStockSizes}</td>
                          <td className="px-3 py-2 text-center">
                            {(() => {
                              const band = BANDS.find((b) => b.key === bandOf(p.breakagePct));
                              return (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${band?.cls || ''}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${band?.dot || 'bg-gray-400'}`} />
                                  {fmtPct(p.breakagePct)}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {p.salePrice != null && p.salePrice < p.price ? (
                              <span><span className="text-gray-400 line-through mr-1 text-xs">{fmtMoney(p.price, sym)}</span><span className="font-semibold text-gray-900">{fmtMoney(p.salePrice, sym)}</span></span>
                            ) : <span className="font-semibold text-gray-900">{fmtMoney(p.price, sym)}</span>}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50/70">
                            <td />
                            <td colSpan={7} className="px-3 py-3">
                              <div className="flex flex-wrap gap-1.5">
                                {p.sizes.map((s, i) => (
                                  <span
                                    key={`${s.size}-${i}`}
                                    title={`${s.size} · ${s.inStock ? 'stokta' : 'stok dışı'}${s.gtin ? ` · GTIN ${s.gtin}` : ''}`}
                                    className={`px-2 py-1 rounded-md text-xs font-medium border ${s.inStock ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}
                                  >
                                    {s.size || '—'}
                                  </span>
                                ))}
                              </div>
                              {p.link && (
                                <a href={p.link} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-blue-600 hover:underline">Ürün sayfası ↗</a>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && selectedBrand?.id && rows.length === 0 && (
            <div className="p-8 text-center text-gray-500">Eşleşen ürün yok.</div>
          )}
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-lg font-bold text-gray-900 tabular-nums">{value}</div>
      </div>
    </div>
  );
}
